import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import OtpModal from './OtpModal';
import DocumentUploadField from './common/DocumentUploadField';
import DocumentViewerModal from './common/DocumentViewerModal';
import config from '../config';
import publicIpService from '../utils/publicIpService';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    city: '',
    state: '',
    country: '',
    zip: '',
    businessName: '',
    panNumber: '',
    gstNumber: '',
    udyamAadharRegNo: '',
    panCardImage: '',
    gstImage: '',
    udyamAadharImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, imageUrl: '', title: '' });
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    // PAN Card image is mandatory because PAN Number is mandatory on registration
    if (!formData.panCardImage || formData.panCardImage.trim() === '') {
      toast.error('Please upload your PAN card document image');
      return false;
    }

    // GST validation: if GST number is entered, GST certificate image is mandatory
    if (formData.gstNumber && formData.gstNumber.trim() !== '') {
      if (!formData.gstImage || formData.gstImage.trim() === '') {
        toast.error('Please upload the GST Certificate image for the entered GST Number');
        return false;
      }
    } else if (formData.gstImage && formData.gstImage.trim() !== '') {
      toast.error('Please enter the GST Number for the uploaded GST Certificate');
      return false;
    }

    // Udyam Aadhaar validation: if Udyam number is entered, Udyam document image is mandatory
    if (formData.udyamAadharRegNo && formData.udyamAadharRegNo.trim() !== '') {
      if (!formData.udyamAadharImage || formData.udyamAadharImage.trim() === '') {
        toast.error('Please upload the Udyam Aadhaar document image for the entered Udyam Number');
        return false;
      }
    } else if (formData.udyamAadharImage && formData.udyamAadharImage.trim() !== '') {
      toast.error('Please enter the Udyam Aadhaar Registration Number for the uploaded document');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...publicIpService.getPublicIpHeader()
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success && data.requiresOtp) {
        setUserEmail(formData.email);
        setShowOtpModal(true);
        toast.success('Registration successful! Please verify your email with the OTP sent.');
      } else {
        if (data.errors && Object.keys(data.errors).length > 0) {
          const errorMessages = Object.values(data.errors).join(' | ');
          toast.error(errorMessages);
        } else {
          toast.error(data.message || 'Registration failed');
        }
      }
    } catch (error) {
      toast.error('Registration failed. Please try again.');
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleRegisterSuccess = () => {
    setShowOtpModal(false);
    toast.success('Registration completed successfully!');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Hotel Management System</h1>
          <p className="text-gray-600">Join our hotel management platform</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h2>
            <p className="text-gray-600">Fill in your details to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Account Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Business Information & Document Uploads */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Name *</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Acme Travel Services Pvt Ltd"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 font-medium"
                />
              </div>

              {/* PAN Number & PAN Card Image Upload */}
              <DocumentUploadField
                label="PAN Number"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                imageValue={formData.panCardImage}
                onImageChange={(img) => setFormData((prev) => ({ ...prev, panCardImage: img }))}
                required={true}
                imageRequired={true}
                placeholder="e.g. ABCDE1234F"
                documentTitle="PAN Card"
                helpText="Valid 10-character PAN number and PAN card photo are mandatory"
                onPreviewImage={(url, title) => setPreviewModal({ isOpen: true, imageUrl: url, title })}
              />

              {/* GST Number & GST Certificate Upload */}
              <DocumentUploadField
                label="GST Number (Optional)"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                imageValue={formData.gstImage}
                onImageChange={(img) => setFormData((prev) => ({ ...prev, gstImage: img }))}
                required={false}
                imageRequired={Boolean(formData.gstNumber && formData.gstNumber.trim() !== '')}
                placeholder="e.g. 22AAAAA0000A1Z5"
                documentTitle="GST Certificate"
                helpText="If GST number is provided, uploading certificate photo is mandatory"
                onPreviewImage={(url, title) => setPreviewModal({ isOpen: true, imageUrl: url, title })}
              />

              {/* Udyam Aadhar Reg No & Udyam Document Upload */}
              <DocumentUploadField
                label="Udyam Aadhar Reg. No. (Optional)"
                name="udyamAadharRegNo"
                value={formData.udyamAadharRegNo}
                onChange={handleChange}
                imageValue={formData.udyamAadharImage}
                onImageChange={(img) => setFormData((prev) => ({ ...prev, udyamAadharImage: img }))}
                required={false}
                imageRequired={Boolean(formData.udyamAadharRegNo && formData.udyamAadharRegNo.trim() !== '')}
                placeholder="e.g. 123456789012 or UDYAM-XX-00-0000000"
                documentTitle="Udyam Aadhaar"
                helpText="If Udyam number is provided, uploading document photo is mandatory"
                onPreviewImage={(url, title) => setPreviewModal({ isOpen: true, imageUrl: url, title })}
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
              
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                Already have an account? Log in
              </button>
            </div>
          </form>
        </div>

        {showOtpModal && (
          <OtpModal
            email={userEmail}
            onClose={() => setShowOtpModal(false)}
            onSuccess={handleRegisterSuccess}
          />
        )}

        {/* Document Full Image Preview Modal */}
        <DocumentViewerModal
          isOpen={previewModal.isOpen}
          onClose={() => setPreviewModal({ isOpen: false, imageUrl: '', title: '' })}
          imageUrl={previewModal.imageUrl}
          title={previewModal.title}
        />
      </div>
    </div>
  );
};

export default RegisterForm;